import http from '@/services/http';

/**
 * API client for the external reconciliation module (`conciliacion-externa`,
 * card #349). Contract: design.md ADR-07 — module `conciliacion`, roles
 * [1,2,8,9], envelope identical to `GET /egresos/pagos`. All calls go
 * through the central `http` instance, never raw axios.
 *
 * `POST /conciliacion/importar/banco` (tramo 2, WU7, gated on a real Galicia
 * .xlsx) is intentionally NOT wired here yet — out of scope for WU6a.
 */
export default {
  importarMercadoPago({ id_cuenta, fecha_desde, fecha_hasta }) {
    return http.post('conciliacion/importar/mercadopago', { id_cuenta, fecha_desde, fecha_hasta });
  },

  obtenerImportaciones(params = {}) {
    return http.get('conciliacion/importaciones', { params });
  },

  obtenerCasos(filtros = {}) {
    return http.get('conciliacion/casos', { params: filtros });
  },

  /**
   * Same endpoint, `export=true`. Ignores pagination server-side (returns
   * ALL rows matching the filters) and responds with `{ success, file_url }`
   * instead of the listing envelope — same convention as
   * pagosEgresosService.exportarListado.
   */
  exportarCasos(filtros = {}) {
    return http.get('conciliacion/casos', { params: { ...filtros, export: true } });
  },

  obtenerCaso(idCaso) {
    return http.get(`conciliacion/casos/${idCaso}`);
  },

  vincular(idCaso, { origen_tipo, origen_id }) {
    return http.post(`conciliacion/casos/${idCaso}/vincular`, { origen_tipo, origen_id });
  },

  reimputar(idCaso, { origen_tipo, origen_id, motivo }) {
    return http.post(`conciliacion/casos/${idCaso}/reimputar`, { origen_tipo, origen_id, motivo });
  },

  crearMovimiento(idCaso, { observacion }) {
    return http.post(`conciliacion/casos/${idCaso}/crear-movimiento`, { observacion });
  },

  ignorar(idCaso, { motivo }) {
    return http.post(`conciliacion/casos/${idCaso}/ignorar`, { motivo });
  },

  /**
   * Defaults de matching por tenant, fila única de `tbl_config_conciliacion`
   * (WU6c, CFG-002). El PUT sólo acepta un subconjunto whitelisteado de 7
   * columnas — enviar de más no rompe nada, el backend las ignora.
   */
  obtenerConfig() {
    return http.get('conciliacion/config');
  },

  actualizarConfig(datos) {
    return http.put('conciliacion/config', datos);
  },

  /**
   * Pertenencia por sucursal/caja de Mercado Pago (WU8): dos tenants comparten UNA cuenta
   * de MP, cada uno declara cuáles sucursales/cajas son propias para que el importador no
   * fabrique casos falsos con movimientos del otro tenant.
   *
   * Live desde MP (no persistido) — sin `id_cuenta`: el catálogo de sucursales/cajas es de
   * la cuenta de MP compartida, no de una cuenta interna puntual.
   */
  listarPuntosVentaMP() {
    return http.get('conciliacion/mp/puntos-venta');
  },

  obtenerPuntosVentaConfig(id_cuenta) {
    return http.get('conciliacion/mp/puntos-venta/config', { params: { id_cuenta } });
  },

  /** Reemplaza el set completo de puntos configurados para la cuenta (no un merge parcial). */
  guardarPuntosVentaConfig({ id_cuenta, puntos }) {
    return http.put('conciliacion/mp/puntos-venta/config', { id_cuenta, puntos });
  },

  /**
   * Líneas externas por alcance (S9.1 envelope). Usado por WU8 con `alcance: 'fuera_alcance'`
   * para listar lo que el importador dejó afuera por no pertenecer a ningún punto configurado.
   */
  listarLineas(params = {}) {
    return http.get('conciliacion/lineas', { params });
  },

  /** Re-evalúa el alcance de un lote ya importado contra la configuración ACTUAL de puntos de
   * venta (útil después de cambiar qué sucursales/cajas son propias). */
  reevaluarAlcance(idImportacion) {
    return http.post(`conciliacion/importaciones/${idImportacion}/reevaluar-alcance`);
  },
};
